import { useState } from 'react'
import Image from 'next/image'

interface Props {
  title: string
  description: string
  list: StrapiAgendaCardComponent[]
}

const timeOfDayOptions = ['Morning', 'Noon', 'Afternoon']

function Agenda({ title, description, list }: Props) {
  const [activeTab, setActiveTab] = useState('Morning')
  const filteredList = list.filter(item => item.timeOfDay === activeTab)

  return (
    <section id="agenda" className="bg-[#090909] text-white relative">
      <Image
        src="/assets/index-25/decode-bg-img.png"
        alt="decode-bg-img"
        height="955"
        width="1441"
        className="absolute w-full h-full object-top"
      />
      <div className="container | relative z-10 mx-auto py-10 md:py-18">
        <div className="max-w-4xl mb-12 md:mb-20">
          <div className="mb-7 md:mb-10 md:max-w-col-9">
            <h1 className="text-4xl md:text-5xl font-medium">{title}</h1>
          </div>
          <p className="text-sm md:text-2xl text-gray-40 tracking-tighter">
            {description}
          </p>
        </div>

        <div className="flex justify-center gap-9 md:gap-24 | text-xl | mb-8 md:mb-10">
          {timeOfDayOptions.map(tab => (
            <button
              key={tab}
              className={`pb-2 border-b-2 transition ${
                activeTab === tab
                  ? 'border-[#4B77F2] font-bold'
                  : 'border-transparent text-gray-400'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-10">
          {filteredList.map(card => (
            <div
              key={card.id}
              className="bg-linear-to-b from-[#2938a2] to-[#0d0b34] rounded-xl p-8 shadow-lg flex flex-col md:flex-row"
            >
              {/* Left Section */}
              <div className="flex flex-col items-start justify-start md:w-1/4 mb-4 md:mb-0 md:mr-8">
                <span className="hidden md:block text-lg md:text-xl font-semibold md:mb-7">
                  {card.time}
                </span>
                {card.tag ? (
                  <span className="px-3 py-1 rounded border border-white text-xs font-medium uppercase mb-2">
                    {card.tag}
                  </span>
                ) : null}
              </div>
              {/* Right Section */}
              <div className="flex-1 flex flex-col justify-center">
                <span className="block md:hidden text-sm text-[#D8D5D5] md:text-white md:text-xl font-semibold mb-4 md:mb-7">
                  {card.time}
                </span>
                <div className="mb-6 md:mb-8">
                  <h3 className="text-base md:text-2xl font-semibold | mb-2 max-w-col-4">
                    {card.title}
                  </h3>
                  <p className="text-caption md:text-lg text-gray-40 md:mb-2 | max-w-col-6">
                    {card.description}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 md:flex md:flex-wrap md:gap-6">
                  {card.profile?.map(profile => (
                    <div
                      key={profile.id}
                      className="flex justify-between gap-2 md:gap-4"
                    >
                      {profile.image?.data?.attributes?.url && (
                        <Image
                          src={profile.image.data.attributes.url}
                          alt={profile.name}
                          width={profile.image.data.attributes.width}
                          height={profile.image.data.attributes.height}
                          className="w-14 h-14 md:w-18 md:h-18 "
                        />
                      )}
                      <div className="flex flex-col justify-end md:gap-3">
                        <span className="text-xs md:text-lg | w-20">
                          {profile.name}
                        </span>
                        {profile.companyImage?.data?.attributes?.url && (
                          <Image
                            src={profile.companyImage.data.attributes.url}
                            alt="company"
                            width={profile.companyImage.data.attributes.width}
                            height={profile.companyImage.data.attributes.height}
                            className="w-18"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Agenda
